import {
  PRODUCT_FEEDBACK_LIMITS,
  type ProductFeedbackDiagnosticEvent,
  type ProductFeedbackDiagnosticLevel,
  type ProductFeedbackDiagnosticSource,
} from '../types/productFeedback';

const REDACTED = '[REDACTED]';
const OMITTED_MESSAGE = '[non-scalar diagnostic omitted]';
const TECHNICAL_SCALAR_FIELDS = ['code', 'status', 'statusText', 'type', 'eventType'] as const;

const SENSITIVE_KEY_VALUE_PATTERN =
  /\b(password|passwd|credential|token|authorization|cookie|secret|api[\s_-]?key|private[\s_-]?key)\b\s*([:=])\s*((?:Bearer\s+)?[^\s,;]+)/gi;
const SENSITIVE_FIELD_NAME_PATTERN =
  /^(?:password|passwd|credential|token|authorization|cookie|secret|api[\s_-]?key|private[\s_-]?key)$/i;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g;
const FIREBASE_KEY_PATTERN = /\b(?:AIza[A-Za-z0-9_-]{20,}|1\/\/[A-Za-z0-9_-]{20,})\b/g;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_PATTERN = /\+?\d(?:[\s().-]?\d){8,}/g;
const ADDRESS_PATTERN =
  /\b(?:ul\.?|ulica|aleja|al\.?|osiedle|os\.?|plac|pl\.?)\s+[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż][A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż\s.'-]{1,60}\s+\d+[A-Za-z]?(?:\/\d+)?\b/gi;

export interface ProductFeedbackDiagnosticEventInput {
  source: ProductFeedbackDiagnosticSource;
  level: ProductFeedbackDiagnosticLevel;
  occurredAtClient: string;
  routePath: string;
  arguments: readonly unknown[];
}

export interface SanitizedProductFeedbackDiagnosticEvent {
  event: ProductFeedbackDiagnosticEvent;
  redactionCount: number;
  omittedObjectCount: number;
  truncated: boolean;
}

interface RedactedText {
  value: string;
  redactionCount: number;
}

interface SanitizedArgument {
  messageCandidate: string | null;
  details: string[];
  stack: string | null;
  redactionCount: number;
  omittedObjectCount: number;
}

export const getProductFeedbackUtf8ByteLength = (value: string): number => {
  let bytes = 0;

  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code <= 0x7f) {
      bytes += 1;
    } else if (code <= 0x7ff) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff && index + 1 < value.length) {
      const next = value.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        index += 1;
      } else {
        bytes += 3;
      }
    } else {
      bytes += 3;
    }
  }

  return bytes;
};

const sliceToUtf8Bytes = (value: string, maxBytes: number): string => {
  let bytes = 0;
  let end = 0;

  while (end < value.length) {
    const code = value.charCodeAt(end);
    let characterBytes: number;
    let characterLength = 1;

    if (code <= 0x7f) {
      characterBytes = 1;
    } else if (code <= 0x7ff) {
      characterBytes = 2;
    } else if (code >= 0xd800 && code <= 0xdbff && end + 1 < value.length) {
      const next = value.charCodeAt(end + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        characterBytes = 4;
        characterLength = 2;
      } else {
        characterBytes = 3;
      }
    } else {
      characterBytes = 3;
    }

    if (bytes + characterBytes > maxBytes) {
      break;
    }

    bytes += characterBytes;
    end += characterLength;
  }

  return value.slice(0, end);
};

export const truncateProductFeedbackUtf8 = (value: string, maxBytes: number): string => {
  if (maxBytes <= 0) {
    return '';
  }

  if (getProductFeedbackUtf8ByteLength(value) <= maxBytes) {
    return value;
  }

  const suffix = '…';
  const suffixBytes = getProductFeedbackUtf8ByteLength(suffix);
  if (suffixBytes > maxBytes) {
    return sliceToUtf8Bytes(value, maxBytes);
  }

  return `${sliceToUtf8Bytes(value, maxBytes - suffixBytes)}${suffix}`;
};

const replaceAndCount = (
  input: string,
  pattern: RegExp,
  replacement: string | ((...matches: string[]) => string)
): RedactedText => {
  let redactionCount = 0;
  const value = input.replace(pattern, (...matches: string[]) => {
    redactionCount += 1;
    return typeof replacement === 'string' ? replacement : replacement(...matches);
  });

  return { value, redactionCount };
};

export const redactProductFeedbackText = (input: string): RedactedText => {
  let value = input.replace(/\r\n?/g, '\n');
  let redactionCount = 0;

  const apply = (pattern: RegExp, replacement: string | ((...matches: string[]) => string)) => {
    const result = replaceAndCount(value, pattern, replacement);
    value = result.value;
    redactionCount += result.redactionCount;
  };

  apply(SENSITIVE_KEY_VALUE_PATTERN, (_match, key, separator) => `${key}${separator}${REDACTED}`);
  apply(BEARER_PATTERN, REDACTED);
  apply(JWT_PATTERN, REDACTED);
  apply(FIREBASE_KEY_PATTERN, REDACTED);
  apply(EMAIL_PATTERN, REDACTED);
  apply(PHONE_PATTERN, REDACTED);
  apply(ADDRESS_PATTERN, REDACTED);

  return { value, redactionCount };
};

const sanitizeRoutePath = (input: unknown): RedactedText => {
  if (typeof input !== 'string' || input.trim() === '') {
    return { value: '/', redactionCount: 0 };
  }

  const trimmed = input.trim();
  let path = trimmed;

  try {
    if (/^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)) {
      path = new URL(trimmed).pathname;
    }
  } catch {
    path = '/';
  }

  path = path.split(/[?#]/, 1)[0] || '/';
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  const redacted = redactProductFeedbackText(path);
  return {
    value: redacted.value.slice(0, PRODUCT_FEEDBACK_LIMITS.maxRouteCharacters),
    redactionCount: redacted.redactionCount,
  };
};

export const normalizeProductFeedbackRoutePath = (input: unknown): string =>
  sanitizeRoutePath(input).value;

const normalizeIsoTimestamp = (value: string): string => {
  const timestamp = new Date(value);
  return Number.isNaN(timestamp.getTime()) ? new Date(0).toISOString() : timestamp.toISOString();
};

const isDomNode = (value: unknown): value is Node => {
  try {
    return typeof Node !== 'undefined' && value instanceof Node;
  } catch {
    return false;
  }
};

const isDomEvent = (value: unknown): value is Event => {
  try {
    return typeof Event !== 'undefined' && value instanceof Event;
  } catch {
    return false;
  }
};

const sanitizeLimitedText = (value: string, maxBytes: number): RedactedText => {
  const redacted = redactProductFeedbackText(value);
  return {
    value: truncateProductFeedbackUtf8(redacted.value, maxBytes),
    redactionCount: redacted.redactionCount,
  };
};

const getErrorStringProperty = (
  error: Error,
  field: 'name' | 'message' | 'stack'
): string | null => {
  let current: object | null = error;

  while (current) {
    const descriptor = Object.getOwnPropertyDescriptor(current, field);
    if (descriptor) {
      return 'value' in descriptor && typeof descriptor.value === 'string'
        ? descriptor.value
        : null;
    }
    current = Object.getPrototypeOf(current);
  }

  return null;
};

const sanitizeErrorCause = (cause: unknown): SanitizedArgument => {
  if (cause instanceof Error) {
    const name = sanitizeLimitedText(
      getErrorStringProperty(cause, 'name') || 'Error',
      PRODUCT_FEEDBACK_LIMITS.maxDetailBytes
    );
    const message = sanitizeLimitedText(
      getErrorStringProperty(cause, 'message') || 'No error message',
      PRODUCT_FEEDBACK_LIMITS.maxDetailBytes
    );
    return {
      messageCandidate: null,
      details: [`cause=${name.value}: ${message.value}`],
      stack: null,
      redactionCount: name.redactionCount + message.redactionCount,
      omittedObjectCount: 0,
    };
  }

  if (typeof cause === 'string' || typeof cause === 'number' || typeof cause === 'boolean') {
    const sanitized = sanitizeLimitedText(String(cause), PRODUCT_FEEDBACK_LIMITS.maxDetailBytes);
    return {
      messageCandidate: null,
      details: [`cause=${sanitized.value}`],
      stack: null,
      redactionCount: sanitized.redactionCount,
      omittedObjectCount: 0,
    };
  }

  return {
    messageCandidate: null,
    details: [],
    stack: null,
    redactionCount: 0,
    omittedObjectCount: cause == null ? 0 : 1,
  };
};

const sanitizeError = (error: Error): SanitizedArgument => {
  const errorName = getErrorStringProperty(error, 'name');
  const errorMessage = getErrorStringProperty(error, 'message');
  const errorStack = getErrorStringProperty(error, 'stack');
  const name = sanitizeLimitedText(errorName || 'Error', PRODUCT_FEEDBACK_LIMITS.maxDetailBytes);
  const message = sanitizeLimitedText(
    errorMessage || 'No error message',
    PRODUCT_FEEDBACK_LIMITS.maxMessageBytes
  );
  const stack = errorStack
    ? sanitizeLimitedText(errorStack, PRODUCT_FEEDBACK_LIMITS.maxStackBytes)
    : null;
  const codeDescriptor = Object.getOwnPropertyDescriptor(error, 'code');
  const causeDescriptor = Object.getOwnPropertyDescriptor(error, 'cause');
  const details = [`errorName=${name.value}`];
  let redactionCount = name.redactionCount + message.redactionCount + (stack?.redactionCount || 0);
  let omittedObjectCount = 0;

  if (
    codeDescriptor &&
    'value' in codeDescriptor &&
    ['string', 'number', 'boolean'].includes(typeof codeDescriptor.value)
  ) {
    const code = sanitizeLimitedText(
      String(codeDescriptor.value),
      PRODUCT_FEEDBACK_LIMITS.maxDetailBytes
    );
    details.push(`code=${code.value}`);
    redactionCount += code.redactionCount;
  }

  if (causeDescriptor && 'value' in causeDescriptor) {
    const cause = sanitizeErrorCause(causeDescriptor.value);
    details.push(...cause.details);
    redactionCount += cause.redactionCount;
    omittedObjectCount += cause.omittedObjectCount;
  }

  return {
    messageCandidate: message.value,
    details,
    stack: stack?.value || null,
    redactionCount,
    omittedObjectCount,
  };
};

const sanitizeTechnicalObject = (value: object): SanitizedArgument => {
  const details: string[] = [];
  let redactionCount = 0;
  let omittedObjectCount = 0;
  let ownKeys: string[] = [];

  try {
    ownKeys = Object.keys(value);
  } catch {
    return {
      messageCandidate: null,
      details: [],
      stack: null,
      redactionCount: 0,
      omittedObjectCount: 1,
    };
  }

  redactionCount += ownKeys.filter((key) => SENSITIVE_FIELD_NAME_PATTERN.test(key)).length;

  for (const field of TECHNICAL_SCALAR_FIELDS) {
    const descriptor = Object.getOwnPropertyDescriptor(value, field);
    if (!descriptor || !('value' in descriptor)) {
      continue;
    }

    const fieldValue = descriptor.value;
    if (
      fieldValue !== null &&
      typeof fieldValue !== 'string' &&
      typeof fieldValue !== 'number' &&
      typeof fieldValue !== 'boolean'
    ) {
      omittedObjectCount = 1;
      continue;
    }

    const sanitized = sanitizeLimitedText(
      String(fieldValue),
      PRODUCT_FEEDBACK_LIMITS.maxDetailBytes
    );
    details.push(`${field}=${sanitized.value}`);
    redactionCount += sanitized.redactionCount;
  }

  if (
    ownKeys.some((key) => !(TECHNICAL_SCALAR_FIELDS as readonly string[]).includes(key)) ||
    details.length === 0
  ) {
    omittedObjectCount = 1;
  }

  return {
    messageCandidate: null,
    details,
    stack: null,
    redactionCount,
    omittedObjectCount,
  };
};

const sanitizeArgument = (value: unknown): SanitizedArgument => {
  if (value instanceof Error) {
    return sanitizeError(value);
  }

  if (isDomNode(value)) {
    const tagName = value instanceof Element ? value.tagName.toLowerCase() : value.nodeName;
    return {
      messageCandidate: null,
      details: [`domNode=${tagName}`],
      stack: null,
      redactionCount: 0,
      omittedObjectCount: 0,
    };
  }

  if (isDomEvent(value)) {
    return {
      messageCandidate: null,
      details: [`eventType=${value.type || 'unknown'}`],
      stack: null,
      redactionCount: 0,
      omittedObjectCount: 0,
    };
  }

  if (typeof value === 'string') {
    const sanitized = sanitizeLimitedText(value, PRODUCT_FEEDBACK_LIMITS.maxDetailBytes);
    return {
      messageCandidate: sanitized.value || null,
      details: [],
      stack: null,
      redactionCount: sanitized.redactionCount,
      omittedObjectCount: 0,
    };
  }

  if (typeof value === 'number' || typeof value === 'boolean' || value == null) {
    return {
      messageCandidate: String(value),
      details: [],
      stack: null,
      redactionCount: 0,
      omittedObjectCount: 0,
    };
  }

  if (typeof value === 'object') {
    return sanitizeTechnicalObject(value);
  }

  return {
    messageCandidate: null,
    details: [],
    stack: null,
    redactionCount: 0,
    omittedObjectCount: 0,
  };
};

const getEventBytes = (event: ProductFeedbackDiagnosticEvent): number =>
  getProductFeedbackUtf8ByteLength(JSON.stringify(event));

const fitEventToLimit = (
  event: ProductFeedbackDiagnosticEvent
): { event: ProductFeedbackDiagnosticEvent; truncated: boolean } => {
  const fitted: ProductFeedbackDiagnosticEvent = {
    ...event,
    details: [...event.details],
    sources: [...event.sources],
  };
  let truncated = false;

  while (
    fitted.details.length > 0 &&
    getEventBytes(fitted) > PRODUCT_FEEDBACK_LIMITS.maxDiagnosticEventBytes
  ) {
    fitted.details.pop();
    truncated = true;
  }

  if (fitted.stack && getEventBytes(fitted) > PRODUCT_FEEDBACK_LIMITS.maxDiagnosticEventBytes) {
    const overflow = getEventBytes(fitted) - PRODUCT_FEEDBACK_LIMITS.maxDiagnosticEventBytes;
    const target = Math.max(0, getProductFeedbackUtf8ByteLength(fitted.stack) - overflow - 16);
    fitted.stack = truncateProductFeedbackUtf8(fitted.stack, target) || null;
    truncated = true;
  }

  if (getEventBytes(fitted) > PRODUCT_FEEDBACK_LIMITS.maxDiagnosticEventBytes) {
    const overflow = getEventBytes(fitted) - PRODUCT_FEEDBACK_LIMITS.maxDiagnosticEventBytes;
    const target = Math.max(32, getProductFeedbackUtf8ByteLength(fitted.message) - overflow - 16);
    fitted.message = truncateProductFeedbackUtf8(fitted.message, target) || '[truncated]';
    truncated = true;
  }

  if (getEventBytes(fitted) > PRODUCT_FEEDBACK_LIMITS.maxDiagnosticEventBytes) {
    fitted.details = [];
    fitted.stack = null;
    fitted.message = '[truncated]';
    truncated = true;
  }

  return { event: fitted, truncated };
};

export const sanitizeProductFeedbackDiagnosticEvent = (
  input: ProductFeedbackDiagnosticEventInput
): SanitizedProductFeedbackDiagnosticEvent => {
  const limitedArguments = input.arguments.slice(
    0,
    PRODUCT_FEEDBACK_LIMITS.maxSerializedArgumentsPerEvent
  );
  const details: string[] = [];
  let message: string | null = null;
  let stack: string | null = null;
  const sanitizedRoute = sanitizeRoutePath(input.routePath);
  let redactionCount = sanitizedRoute.redactionCount;
  let omittedObjectCount = Math.max(0, input.arguments.length - limitedArguments.length);

  for (const value of limitedArguments) {
    const sanitized = sanitizeArgument(value);
    redactionCount += sanitized.redactionCount;
    omittedObjectCount += sanitized.omittedObjectCount;

    if (!message && sanitized.messageCandidate) {
      message = truncateProductFeedbackUtf8(
        sanitized.messageCandidate,
        PRODUCT_FEEDBACK_LIMITS.maxMessageBytes
      );
    } else if (sanitized.messageCandidate) {
      details.push(
        truncateProductFeedbackUtf8(
          sanitized.messageCandidate,
          PRODUCT_FEEDBACK_LIMITS.maxDetailBytes
        )
      );
    }

    details.push(...sanitized.details);
    if (!stack && sanitized.stack) {
      stack = sanitized.stack;
    }
  }

  const event: ProductFeedbackDiagnosticEvent = {
    source: input.source,
    level: input.level,
    occurredAtClient: normalizeIsoTimestamp(input.occurredAtClient),
    routePath: sanitizedRoute.value,
    message: message || OMITTED_MESSAGE,
    details,
    stack,
    sources: [input.source],
    occurrences: 1,
  };
  const fitted = fitEventToLimit(event);

  return {
    event: fitted.event,
    redactionCount,
    omittedObjectCount,
    truncated: fitted.truncated || input.arguments.length > limitedArguments.length,
  };
};
